import React from 'react';
import {
  GridColDef,
  GridColumnHeaderParams,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridValueSetterParams,
} from '@mui/x-data-grid-pro';
import GenericGridContent from '../../../components/datagridComponents/GenericGridContent';
import ActionsMenu from '../../../components/datagridComponents/ActionsMenu';
import { nameValidationSchema, passcodeValidationSchema } from './validationSchema';
import { SelectOption } from '../../../types';

const BuildColumns = (
  staffRolesOptions: SelectOption[],
  locations: SelectOption[]
): GridColDef[] => [
  {
    field: 'name',
    flex: 0.5,
    minWidth: 100,
    editable: false,
    sortable: false,
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
      nameValidationSchema
        .isValid({
          name: params?.props?.value,
        })
        .then((valid) => ({ ...params.props, error: !valid }))
        .catch(() => ({ ...params.props })),
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staff.name',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => (
      <GenericGridContent
        content={[
          {
            text: params.value,
            shouldTranslate: false,
            typographyProps: {
              variant: 'body2',
            },
          },
        ]}
      />
    ),
  },
  {
    field: 'mainLocationId',
    flex: 0.5,
    minWidth: 100,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staff.mainLocation',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => {
      let locationName = '';

      locationName = locations?.find(
        (location) => location.value === params?.row?.mainLocationId?.toString()
      )?.label;

      return (
        <GenericGridContent
          content={[
            {
              text: locationName,
              shouldTranslate: false,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      );
    },
    valueSetter: (params: GridValueSetterParams) => ({
      ...params.row,
      mainLocationId: params.value ? +params.value : null,
    }),
  },
  {
    field: 'showAtAllLocations',
    flex: 0.5,
    minWidth: 100,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staff.showAtAllLocations',
            shouldTranslate: true,
            shouldClipContent: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => (
      <GenericGridContent
        content={[
          {
            text: params.value ? 'actions.yes' : 'actions.no',
            shouldTranslate: true,
            typographyProps: {
              variant: 'body2',
            },
          },
        ]}
      />
    ),
    valueSetter: (params: GridValueSetterParams) => ({
      ...params.row,
      showAtAllLocations:
        params.value === '0' || (typeof params.value === 'boolean' && params.value === true),
    }),
  },
  {
    field: 'staffRoleId',
    flex: 0.3,
    minWidth: 80,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staff.roleName',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) => {
      const roleName = staffRolesOptions?.find(
        (role) => role?.value === params?.value?.toString()
      )?.label;

      return params.value != null ? (
        <GenericGridContent
          content={[
            {
              text: roleName,
              shouldTranslate: false,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ) : null;
    },
  },
  {
    field: 'passcode',
    flex: 0.3,
    minWidth: 80,
    editable: false,
    sortable: false,
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
      passcodeValidationSchema
        .isValid({
          passcode: params?.props?.value,
        })
        .then((valid) => ({ ...params.props, error: !valid }))
        .catch(() => ({ ...params.props })),
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'screens.staff.passcode',
            shouldTranslate: true,
            shouldClipContent: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <GenericGridContent
          content={[
            {
              text: '********',
              shouldTranslate: false,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ) : null,
  },
  {
    field: 'actions',
    minWidth: 100,
    editable: false,
    sortable: false,
    renderHeader: (_params: GridColumnHeaderParams) => (
      <GenericGridContent
        content={[
          {
            text: 'common.actions',
            shouldTranslate: true,
            typographyProps: {
              variant: 'boldText',
            },
          },
        ]}
      />
    ),
    align: 'right',
    headerAlign: 'right',
    hideSortIcons: true,
    headerClassName: 'hideRightSeparator',
    cellClassName: 'actionsCell',
    renderCell: (params: GridRenderCellParams) => <ActionsMenu rowId={params.row.id.toString()} />,
  },
];

export default BuildColumns;
